import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient, useMutation } from "@tanstack/react-query";

import { useAnalytics, useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { LayoutRecord } from "@/shared/domain/layouts";
import { layoutQueryKeys } from "@/shared/domain/layouts/hooks";
import { CloseIcon } from "@/shared/foundation/icons/mui";
import { LoggerService } from "@/shared/services/LoggerService";
import { useVizState } from "@/shared/state/visualization";
import { diffVizState } from "@/shared/state/visualization/diffVizState";

import { applyChangesToLayout } from "../../layouts/applyChangesToLayout";
import { useWorkspaceSnapshot } from "../../WorkspaceCtx/hooks";

interface SaveLayoutProps {
  isOpen: boolean;
  layout: LayoutRecord | null;
  onClose: () => void;
  onExited: () => void;
  onAnswered: (answer: Answer, newLayout?: LayoutRecord) => void;
}

interface Answer {
  shouldOverwrite: boolean;
}

export function SaveLayoutConfirmation(props: SaveLayoutProps) {
  const { isOpen, layout, onClose, onExited, onAnswered } = props;

  const { trackEvent } = useAnalytics();

  const saveLayout = useSaveLayout();

  const saveLayoutDefinition = () => {
    if (!layout) {
      return;
    }

    saveLayout.mutate(layout, {
      onSuccess: (newLayout: LayoutRecord) => {
        onAnswered(
          {
            shouldOverwrite: true,
          },
          newLayout,
        );
      },
    });
  };

  const closeDialog = () => {
    trackEvent("SaveLayoutConfirmationCanceled", {
      layoutId: layout?.layout_id ?? "",
      layoutName: layout?.name ?? "",
    });
    onClose();
  };

  return (
    <Dialog
      open={isOpen && layout !== null}
      onClose={closeDialog}
      TransitionProps={{ onExited }}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle id="form-dialog-title" sx={{ pr: 6 }}>
        Save changes to your current layout
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body1">
            This will save changes to <b>&quot;{layout?.name}&quot;</b>. Are you
            sure?
          </Typography>

          {saveLayout.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to overwrite layout. Try again.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <RobotoButton
          eventName="SaveAsNewLayoutClicked"
          onClick={() =>
            onAnswered({
              shouldOverwrite: false,
            })
          }
          type="button"
        >
          No, create a new layout
        </RobotoButton>
        <RobotoButton
          eventName="SaveLayoutClicked"
          eventProperties={{
            layoutId: layout?.layout_id ?? "",
            layoutName: layout?.name ?? "",
          }}
          onClick={saveLayoutDefinition}
          variant="contained"
          type="button"
          disabled={saveLayout.isPending}
          endIcon={
            saveLayout.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {saveLayout.isError ? "Retry" : "Yes"}
        </RobotoButton>
      </DialogActions>
    </Dialog>
  );
}

function useSaveLayout() {
  const { layoutService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const [initialState] = useWorkspaceSnapshot();
  const currentState = useVizState();

  return useMutation({
    mutationFn: async (existingLayout: LayoutRecord) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      // Compute the diff between initial and current vizState to find what has changed
      const diff = diffVizState(initialState, currentState);

      // Calculate the new layout definition from the changeset and the existing layout
      const layoutDefinition = applyChangesToLayout(
        existingLayout.layout_definition,
        existingLayout.schema_version,
        diff,
      );

      LoggerService.log("Saving layout:", layoutDefinition.state);

      return layoutService.updateLayout(
        existingLayout.layout_id,
        {
          layout_definition: layoutDefinition.state,
          schema_version: layoutDefinition.schemaVersion,
        },
        currentOrganization.org_id,
      );
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: layoutQueryKeys.lists(),
      });
    },
  });
}
